import { TemplateResponse } from 'components/ui/composed/template/TemplateContext';
import { ReactElement, useEffect, useState } from 'react';
import { H5 } from 'components/ui/base/typography';
import CreateTemplateDrawer from './components/CreateTemplateDrawer';
import { DeclarationCountry } from 'store/declarations/enums/common/declaration-country';
import { DeclarationInternalType } from 'store/declarations/enums/common/declaration-internal-type';
import { MessageTypes } from '../../store/declarations/enums/common/declaration-types';
import useBreadcrumb from '../../hooks/useBreadcrumb';
import { getTemplates } from 'store/template/client';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { TemplatesOutletContext } from './TemplateView';
import useSession from 'hooks/useSession';
import { checkIsFreightForwarder } from 'config/roles/roles';
import PlusIcon from 'views/declarations/common/box44/components/PlusIcon';
import { StyledButton, StyledTabs } from 'views/declarations/declaration-tabs/declaration-form/Form.styles';
import { isTemplatesTab, TemplatesTab } from './types';
import useProductTemplateList from './hooks/useProductTemplateList';
import DeclarationTemplatesTab from './components/DeclarationTemplatesTab';
import ProductTemplatesTab from './components/ProductTemplatesTab';
import ItemFieldTemplatesTab from './item-field-templates/ItemFieldTemplatesTab';
import CreateItemFieldTemplateDrawer from './item-field-templates/CreateItemFieldTemplateDrawer';
import Segmented from 'components/new-ui/base/segmented/Segmented';

const TemplateDashboard = (): ReactElement => {
    const { userInfo } = useSession();
    const { filters, archived, setArchived } = useOutletContext<TemplatesOutletContext>();
    const { setBreadcrumbRoutes } = useBreadcrumb();
    const [searchParams, setSearchParams] = useSearchParams();

    const { country, internalType, formType } = filters;
    // Owned here because both the product templates tab and the create drawer need to reload it.
    // The tab's table requests the list itself, with its own paging, sorting and search.
    const productTemplateList = useProductTemplateList({
        country,
        internalType,
        messageType: formType,
        autoFetch: false,
    });

    const tabFromUrl = searchParams.get('tab');
    const [mode, setMode] = useState<TemplatesTab>(isTemplatesTab(tabFromUrl) ? tabFromUrl : 'declaration');
    const [isDrawerVisible, setIsDrawerVisible] = useState<boolean>(false);
    const [isItemFieldDrawerVisible, setIsItemFieldDrawerVisible] = useState<boolean>(false);

    const [templates, setTemplates] = useState<TemplateResponse[] | undefined>(undefined);

    const isFreightForwarderRole = checkIsFreightForwarder(userInfo);

    useEffect(() => {
        getTemplates(DeclarationCountry.IRELAND, DeclarationInternalType.IMPORT, 'H1' as MessageTypes, {
            size: 999,
        })?.then((payload) => setTemplates(payload.list));
    }, []);

    const templateOptions = templates?.map((template) => ({
        id: template.id,
        value: template.templateName,
    }));

    useEffect(() => {
        setBreadcrumbRoutes([
            {
                breadcrumbName: 'Templates',
                path: '',
            },
        ]);
    }, [setBreadcrumbRoutes]);

    const handleNewTemplate = () => {
        if (mode === 'item-field') {
            setIsItemFieldDrawerVisible(true);
            return;
        }
        setIsDrawerVisible(true);
    };

    const handleTabChange = (value: string) => {
        if (!isTemplatesTab(value)) return;
        setMode(value);
        filters.changeSearch('');
        setSearchParams({ tab: value });
    };

    return (
        <>
            <div
                style={{
                    marginTop: mode === 'declaration' ? '1.6rem' : '1.8rem',
                    marginBottom: '1.6rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'end',
                }}
            >
                <H5>Templates</H5>
                <div
                    style={{
                        gap: '8px',
                        display: 'flex',
                    }}
                >
                    <Segmented
                        style={{
                            display: mode === 'product' ? 'none' : 'inline-flex',
                        }}
                        options={['Active', 'Archived']}
                        value={archived ? 'Archived' : 'Active'}
                        onChange={(value) => setArchived(value === 'Archived')}
                    />
                    <StyledButton
                        style={{
                            height: '32px',
                            paddingLeft: '16px',
                            paddingRight: '16px',
                            fontSize: '14px',
                            alignItems: 'center',
                            display: 'inline-flex',
                            gap: '8px',
                        }}
                        type="primary"
                        size="large"
                        primary
                        onClick={handleNewTemplate}
                        disabled={archived}
                    >
                        <PlusIcon /> New Template
                    </StyledButton>
                </div>
            </div>

            <StyledTabs onChange={handleTabChange} activeKey={mode} inputMode="url">
                <StyledTabs.TabPane tab="Declaration Templates" key={'declaration'} />
                <StyledTabs.TabPane tab="Product Templates" key={'product'} />
                <StyledTabs.TabPane tab="Item Field Templates" key={'item-field'} />
            </StyledTabs>

            <div>
                {mode === 'declaration' && <DeclarationTemplatesTab filters={filters} archived={archived} />}
                {mode === 'product' && (
                    <ProductTemplatesTab filters={filters} productTemplateList={productTemplateList} />
                )}
                {mode === 'item-field' && (
                    <ItemFieldTemplatesTab
                        search={filters.search}
                        onSearch={filters.changeSearch}
                        archived={archived}
                    />
                )}
            </div>

            <CreateTemplateDrawer
                isFreightForwarder={isFreightForwarderRole}
                visible={isDrawerVisible}
                onClose={() => setIsDrawerVisible(false)}
                templateOptions={templateOptions}
                mode={mode}
                country={country}
                messageType={formType}
                internalType={internalType}
                onProductTemplatesCreated={productTemplateList.refetch}
            />

            <CreateItemFieldTemplateDrawer
                open={isItemFieldDrawerVisible}
                onClose={() => setIsItemFieldDrawerVisible(false)}
                isFreightForwarder={isFreightForwarderRole}
            />
        </>
    );
};

export default TemplateDashboard;
